import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, fileUrl, formatApiError, scadenzaInfo, downloadScheda } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ChevronDown, ChevronRight, Trash2, Receipt, Paperclip, Building2, Package, Award, Download, Send, X, Pencil, FileText, Search, KeyRound, Bell, CheckCircle2, Circle } from "lucide-react";

const SEMAFORO = {
  nuovo: { color: "bg-rose-500", ring: "ring-rose-200", label: "Nuovo", text: "text-rose-700", bg: "bg-rose-50" },
  in_lavorazione: { color: "bg-amber-400", ring: "ring-amber-200", label: "In lavorazione", text: "text-amber-700", bg: "bg-amber-50" },
  completato: { color: "bg-emerald-500", ring: "ring-emerald-200", label: "Completato", text: "text-emerald-700", bg: "bg-emerald-50" },
  annullato: { color: "bg-slate-400", ring: "ring-slate-200", label: "Annullato", text: "text-slate-600", bg: "bg-slate-100" },
};
const Semaforo = ({ stato }) => {
  const s = SEMAFORO[stato] || SEMAFORO.nuovo;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`} data-testid={`order-semaforo-${stato}`}>
      <span className={`h-2.5 w-2.5 rounded-full ${s.color} ring-2 ${s.ring}`} />
      {s.label}
    </span>
  );
};

export default function Orders() {
  const { user } = useAuth();
  const isOwner = user.role !== "partner";
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [ordSearch, setOrdSearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [att, setAtt] = useState(null);  // dialog: {oid, ri, ci, nome, corso, existing, scadenza, file}
  const [staff, setStaff] = useState([]);
  const [fadDlg, setFadDlg] = useState(null);

  const load = () => api.get("/orders").then((r) => { setOrders(r.data); window.dispatchEvent(new Event("badges-refresh")); });
  useEffect(() => {
    load();
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    if (isOwner) api.get("/staff-assegnabili").then((r) => setStaff(r.data)).catch(() => {});
  }, []);
  const companyFor = (o) => companies.find((x) => x.id === o.azienda_id)
    || companies.find((x) => (x.nome || "").toLowerCase() === (o.azienda_nome || "").toLowerCase())
    || { nome: o.azienda_nome };
  const corsiOfCorsista = (o, c) => {
    const keyOf = (x) => (x.codice_fiscale || "").trim().toUpperCase()
      || `${(x.nome || "").trim().toLowerCase()}|${(x.cognome || "").trim().toLowerCase()}`;
    const key = keyOf(c);
    const list = [];
    (o.righe || []).forEach((r) => (r.corsisti || []).forEach((x) => {
      if (keyOf(x) === key && r.servizio_nome) list.push(r.servizio_nome);
    }));
    return [...new Set(list)];
  };
  const scheda = async (o, c) => {
    try { await downloadScheda(c, companyFor(o), corsiOfCorsista(o, c), o.azienda_id); }
    catch (e) { toast.error("Impossibile generare la scheda"); }
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const openAtt = (o, ri, ci, c) => setAtt({
    oid: o.id, ri, ci,
    nome: `${c.nome || ""} ${c.cognome || ""}`.trim() || "corsista",
    corso: o.righe[ri]?.servizio_nome || "",
    existing: c.attestato || null,
    scadenza: c.attestato?.scadenza || "",
    data_emissione: c.attestato?.data_emissione || "",
    file: null,
  });

  const saveAtt = async () => {
    try {
      if (att.file) {
        const fd = new FormData();
        fd.append("file", att.file);
        if (att.scadenza) fd.append("scadenza", att.scadenza);
        if (att.data_emissione) fd.append("data_emissione", att.data_emissione);
        await api.post(`/orders/${att.oid}/righe/${att.ri}/corsisti/${att.ci}/attestato`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Attestato caricato: il partner è stato avvisato");
      } else if (att.existing) {
        await api.put(`/orders/${att.oid}/righe/${att.ri}/corsisti/${att.ci}/attestato/scadenza`, { scadenza: att.scadenza || null });
        toast.success("Scadenza aggiornata");
      } else {
        return toast.error("Seleziona un file da caricare");
      }
      setAtt(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeAtt = async () => {
    if (!window.confirm("Rimuovere questo attestato?")) return;
    try {
      await api.delete(`/orders/${att.oid}/righe/${att.ri}/corsisti/${att.ci}/attestato`);
      toast.success("Attestato rimosso"); setAtt(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStato = async (id, stato) => {
    try {
      await api.put(`/orders/${id}/stato`, { stato });
      toast.success("Stato aggiornato"); load();
      window.dispatchEvent(new Event("badges-refresh"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const assignOrder = async (id, assegnato_id) => {
    try {
      await api.put(`/orders/${id}/stato`, { stato: "in_lavorazione", assegnato_id });
      toast.success(assegnato_id ? "Ordine assegnato" : "Assegnazione rimossa"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async (id) => {
    if (!window.confirm("Creare una vendita da questo ordine? La vendita ha solo scopo contabile: NON segna l'ordine come completato. L'avanzamento resta deciso da te, servizio per servizio.")) return;
    try {
      await api.post(`/orders/${id}/convert`);
      toast.success("Vendita contabile creata. L'avanzamento dell'ordine resta da gestire nei singoli servizi.");
      load();
      window.dispatchEvent(new Event("badges-refresh"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleRiga = async (id, ri, completato) => {
    try {
      await api.put(`/orders/${id}/righe/${ri}/completato`, { completato });
      load();
      window.dispatchEvent(new Event("badges-refresh"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo ordine?")) return;
    await api.delete(`/orders/${id}`); toast.success("Ordine eliminato"); load();
  };

  const [sendDlg, setSendDlg] = useState(null);
  const openSend = (o) => setSendDlg({ oid: o.id, azienda: o.azienda_nome, files: [], messaggio: "" });
  const sendDocs = async () => {
    if (!sendDlg.files.length) return toast.error("Allega almeno un documento");
    const fd = new FormData();
    sendDlg.files.forEach((f) => fd.append("files", f));
    if (sendDlg.messaggio) fd.append("messaggio", sendDlg.messaggio);
    try {
      const r = await api.post(`/orders/${sendDlg.oid}/invia-documenti`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.email_sent
        ? `Inviati ${r.data.count} documenti al partner via email`
        : `${r.data.count} documenti salvati; il partner è stato avvisato in piattaforma`);
      setSendDlg(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const FAD_LINKS = {
    kattedra: "https://formazione.associazioneics.com/login",
    lcr: "https://piattaforma.lcrformazione.com/login",
    safe: "https://fad.safeconsultingsrl.com",
  };
  const FAD_LABELS = { kattedra: "Kattedra", lcr: "LCR Formazione", safe: "Safe Consulting" };
  const slug = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, "");
  const openFad = async (o) => {
    const rows = [];
    (o.righe || []).forEach((r, ri) => (r.corsisti || []).forEach((c, ci) => {
      const cred = c.fad_credenziali || {};
      const defUser = `${slug(c.nome)}.${slug(c.cognome)}`.replace(/^\.|\.$/g, "");
      const piatt = cred.piattaforma || "kattedra";
      rows.push({
        ri, ci, corso: r.servizio_nome || "",
        nome: `${c.nome || ""} ${c.cognome || ""}`.trim() || "corsista",
        piattaforma: piatt,
        link: cred.link || FAD_LINKS[piatt] || "",
        username: cred.username || defUser,
        password: cred.password || defUser,
        email: cred.email || c.email || "",
        sent_at: cred.sent_at || "", sending: false, sollecitando: false,
        invii: c.fad_invii || [],
      });
    }));
    setFadDlg({
      oid: o.id, azienda: o.azienda_nome, rows,
      data_attivazione: "", data_fine: "", tutor_nome: "", tutor_contatto: "",
      email_partner: "", email_partner_custom: false, partnerEmails: [], avvisa_safe: false,
    });
    try {
      const pe = await api.get(`/orders/${o.id}/partner-emails`);
      setFadDlg((d) => (d && d.oid === o.id ? { ...d, partnerEmails: pe.data.emails || [] } : d));
    } catch { /* ignore */ }
  };
  const setFadRow = (i, patch) => setFadDlg((d) => ({ ...d, rows: d.rows.map((r, k) => (k === i ? { ...r, ...patch } : r)) }));
  const onFadPlatform = (i, piattaforma) => {
    const row = fadDlg.rows[i];
    const wasDefault = !row.link || Object.values(FAD_LINKS).includes(row.link);
    setFadRow(i, { piattaforma, ...(wasDefault ? { link: FAD_LINKS[piattaforma] || "" } : {}) });
  };
  const onPartnerEmailChange = (v) => {
    if (v === "__none__") setFadDlg((d) => ({ ...d, email_partner: "", email_partner_custom: false }));
    else if (v === "__custom__") setFadDlg((d) => ({ ...d, email_partner: "", email_partner_custom: true }));
    else setFadDlg((d) => ({ ...d, email_partner: v, email_partner_custom: false }));
  };
  const sendFad = async (i) => {
    const r = fadDlg.rows[i];
    if (!r.email) return toast.error("Inserisci l'email del corsista");
    setFadRow(i, { sending: true });
    try {
      const res = await api.post(`/orders/${fadDlg.oid}/righe/${r.ri}/corsisti/${r.ci}/fad-credenziali`, {
        piattaforma: r.piattaforma, link: r.link, username: r.username, password: r.password, email: r.email,
        email_partner: fadDlg.email_partner, avvisa_safe: fadDlg.avvisa_safe,
        data_attivazione: fadDlg.data_attivazione, data_fine: fadDlg.data_fine,
        tutor_nome: fadDlg.tutor_nome, tutor_contatto: fadDlg.tutor_contatto,
      });
      toast.success(res.data.email_sent ? `Credenziali inviate a ${r.email}` : `Credenziali salvate (email non inviata a ${r.email})`);
      setFadRow(i, { sending: false, sent_at: new Date().toISOString(), invii: [...(r.invii || []), res.data.invio] });
      load();
    } catch (e) { setFadRow(i, { sending: false }); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendSollecito = async (i) => {
    const r = fadDlg.rows[i];
    if (!r.email) return toast.error("Inserisci l'email del corsista");
    setFadRow(i, { sollecitando: true });
    try {
      const res = await api.post(`/orders/${fadDlg.oid}/righe/${r.ri}/corsisti/${r.ci}/fad-sollecito`, {
        email: r.email, email_partner: fadDlg.email_partner, avvisa_safe: fadDlg.avvisa_safe,
        data_fine: fadDlg.data_fine, messaggio: fadDlg.sollecito_msg || "",
      });
      toast.success(res.data.email_sent ? `Sollecito inviato a ${r.email}` : `Sollecito non inviato a ${r.email}`);
      setFadRow(i, { sollecitando: false, invii: [...(r.invii || []), res.data.invio] });
      load();
    } catch (e) { setFadRow(i, { sollecitando: false }); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filteredOrders = useMemo(() => {
    const q = ordSearch.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) => {
      const base = [o.azienda_nome, String(o.numero || "")].some((v) => (v || "").toLowerCase().includes(q));
      const inRighe = (o.righe || []).some((r) =>
        (r.servizio_nome || "").toLowerCase().includes(q) ||
        (r.corsisti || []).some((c) => `${c.nome || ""} ${c.cognome || ""}`.toLowerCase().includes(q)));
      return base || inRighe;
    });
  }, [orders, ordSearch]);

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">{isOwner ? "Ordini dai Partner" : "I Miei Ordini"}</h1>
        <p className="text-sm text-slate-500 mt-1">{isOwner ? "Ordini inviati dai partner: gestiscili, carica gli attestati e trasformali in vendite." : "Gli ordini che hai inviato alla Safe."}</p>
      </div>

      <div className="relative w-full sm:max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={ordSearch} onChange={(e) => setOrdSearch(e.target.value)} data-testid="orders-search"
          placeholder="Cerca per azienda, n° ordine, corso o corsista…" className="pl-9 bg-white" />
        {ordSearch && (
          <button onClick={() => setOrdSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="orders-search-clear">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="space-y-3">
        {filteredOrders.map((o) => {
          const isOpen = !!expanded[o.id];
          const corsistiCount = (o.righe || []).reduce((n, r) => n + (r.corsisti?.length || 0), 0);
          return (
            <Card key={o.id} className="border-slate-200 shadow-sm overflow-hidden" data-testid={`order-card-${o.id}`}>
              <div className="p-4 flex flex-wrap items-center gap-4 cursor-pointer" onClick={() => toggle(o.id)}>
                <button className="text-slate-400" data-testid={`order-toggle-${o.id}`}>{isOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}</button>
                <div className="flex items-center gap-2 min-w-[180px]">
                  <div className="h-9 w-9 rounded-lg bg-dusty-50 text-dusty-600 flex items-center justify-center"><Package className="h-4.5 w-4.5" /></div>
                  <div>
                    <div className="font-semibold text-slate-800 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-slate-400" />{o.azienda_nome}</div>
                    <div className="text-xs text-slate-400">Ordine #{o.numero} · {fmtDate(o.created_at)}</div>
                  </div>
                </div>
                {isOwner && <div className="text-sm text-slate-600">{o.partner_nome}</div>}
                <div className="text-xs text-slate-500">{o.righe?.length || 0} corsi · {corsistiCount} corsisti</div>
                {o.allegati?.length > 0 && <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" />{o.allegati.length}</div>}
                <Semaforo stato={o.stato} />
                {(o.righe || []).length > 0 && (() => {
                  const done = (o.righe || []).filter((r) => r.completato).length;
                  const tot = (o.righe || []).length;
                  const all = done === tot;
                  return (
                    <span data-testid={`order-progress-${o.id}`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${all ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      {all ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />} {done}/{tot} servizi completati
                    </span>
                  );
                })()}
                {o.assegnato_nome && (
                  <div className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid={`order-assegnato-${o.id}`}>
                    In carico a: <span className="font-medium text-slate-700">{o.assegnato_nome}</span>
                  </div>
                )}
                <div className="ml-auto flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {o.stato === "nuovo" && (
                    <Button size="sm" variant="outline" onClick={() => navigate("/catalogo", { state: { editOrder: o } })} data-testid={`order-edit-${o.id}`}>
                      <Pencil className="h-4 w-4 mr-1" /> Modifica
                    </Button>
                  )}
                  {isOwner ? (
                    <Select value={o.stato} onValueChange={(v) => setStato(o.id, v)}>
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid={`order-stato-select-${o.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nuovo">Nuovo</SelectItem>
                        <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
                        <SelectItem value="completato">Completato</SelectItem>
                        <SelectItem value="annullato">Annullato</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : null}
                  {isOwner && o.stato === "in_lavorazione" && (
                    <Select value={o.assegnato_id || "none"} onValueChange={(v) => assignOrder(o.id, v === "none" ? "" : v)}>
                      <SelectTrigger className="h-8 w-44 text-xs" data-testid={`order-assign-select-${o.id}`}>
                        <SelectValue placeholder="Assegna a…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non assegnato</SelectItem>
                        {staff.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.nome}{s.role === "owner" ? " (Titolare)" : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {isOwner && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openSend(o)} data-testid={`order-send-docs-${o.id}`}>
                        <Send className="h-4 w-4 mr-1" /> Documenti da firmare
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openFad(o)} data-testid={`order-fad-${o.id}`}>
                        <KeyRound className="h-4 w-4 mr-1" /> Credenziali FAD
                      </Button>
                      <Button size="sm" variant="outline" disabled={!!o.sale_id} onClick={() => convert(o.id)} data-testid={`order-convert-${o.id}`}>
                        <Receipt className="h-4 w-4 mr-1" /> {o.sale_id ? "Vendita creata" : "Crea vendita"}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => del(o.id)} data-testid={`order-delete-${o.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </>
                  )}
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-slate-100 bg-slate-50/50 p-4 space-y-4" data-testid={`order-detail-${o.id}`}>
                  {(o.righe || []).map((r, ri) => (
                    <div key={ri} className={`bg-white rounded-lg border p-3 ${r.completato ? "border-emerald-200" : "border-slate-200"}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="font-medium text-slate-800 text-sm flex items-center gap-2">
                          {r.servizio_nome} <span className="text-slate-400 font-normal">× {r.quantita}</span>
                          <span data-testid={`riga-stato-${o.id}-${ri}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${r.completato ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                            {r.completato ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />} {r.completato ? "Completato" : "In corso"}
                          </span>
                        </div>
                        {isOwner && (
                          <Button size="sm" variant={r.completato ? "outline" : "default"}
                            className={r.completato ? "h-7 text-xs border-slate-300" : "h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"}
                            onClick={() => toggleRiga(o.id, ri, !r.completato)} data-testid={`riga-toggle-${o.id}-${ri}`}>
                            {r.completato ? "Segna da completare" : "Segna completato"}
                          </Button>
                        )}
                      </div>
                      {r.corsisti?.length > 0 && (
                        <div className="mt-2 overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead><tr className="text-left text-slate-400">
                              <th className="py-1 pr-3">Nome</th><th className="py-1 pr-3">Cognome</th><th className="py-1 pr-3">Cod. Fiscale</th>
                              <th className="py-1 pr-3">Email</th><th className="py-1 pr-3">Telefono</th><th className="py-1 pr-3">Nascita</th>
                              <th className="py-1 pr-3">Mansione</th><th className="py-1 pr-3">ATECO</th><th className="py-1 pr-3">Tipo</th>
                              <th className="py-1 pr-3">Scheda</th>
                              <th className="py-1 pr-3">Attestato</th>
                            </tr></thead>
                            <tbody>
                              {r.corsisti.map((c, ci) => (
                                <tr key={ci} className="border-t border-slate-100 text-slate-600">
                                  <td className="py-1 pr-3">{c.nome || "—"}</td><td className="py-1 pr-3">{c.cognome || "—"}</td>
                                  <td className="py-1 pr-3">{c.codice_fiscale || "—"}</td><td className="py-1 pr-3">{c.email || "—"}</td>
                                  <td className="py-1 pr-3">{c.telefono || "—"}</td>
                                  <td className="py-1 pr-3">{[c.data_nascita, c.luogo_nascita].filter(Boolean).join(" · ") || "—"}</td>
                                  <td className="py-1 pr-3">{c.mansione || "—"}</td>
                                  <td className="py-1 pr-3">{c.codice_ateco || "—"}</td>
                                  <td className="py-1 pr-3">{ {datore_lavoro: "Datore di Lavoro", dipendente: "Dipendente", privato: "Privato"}[c.tipo] || "—"}</td>
                                  <td className="py-1 pr-3">
                                    <button onClick={() => scheda(o, c)} className="inline-flex items-center gap-1 text-dusty-700 hover:underline" data-testid={`order-scheda-${o.id}-${ri}-${ci}`}>
                                      <FileText className="h-3.5 w-3.5" /> Scheda di iscrizione
                                    </button>
                                  </td>
                                  <td className="py-1 pr-3">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {isOwner && c.attestato ? (
                                        <>
                                          <a href={fileUrl(c.attestato.storage_path)} target="_blank" rel="noreferrer"
                                            className="inline-flex items-center gap-1 text-emerald-700 hover:underline" data-testid={`attestato-download-${o.id}-${ri}-${ci}`}>
                                            <Download className="h-3.5 w-3.5" /> Scarica
                                          </a>
                                          {c.attestato.scadenza && (
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${scadenzaInfo(c.attestato.scadenza).cls}`} data-testid={`attestato-scad-${o.id}-${ri}-${ci}`}>
                                              {scadenzaInfo(c.attestato.scadenza).label}
                                            </span>
                                          )}
                                        </>
                                      ) : (!isOwner && (
                                        c.attestato
                                          ? <span className="text-xs text-slate-400">In «I Miei Attestati»</span>
                                          : <span className="text-slate-300">—</span>
                                      ))}
                                      {isOwner && (
                                        <button onClick={() => openAtt(o, ri, ci, c)}
                                          className="inline-flex items-center gap-1 text-dusty-700 hover:underline" data-testid={`attestato-manage-${o.id}-${ri}-${ci}`}>
                                          <Award className="h-3.5 w-3.5" /> {c.attestato ? "Gestisci" : "Allega"}
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                  {o.note && <div className="text-sm text-slate-600"><span className="font-medium text-slate-700">Note:</span> {o.note}</div>}
                  {o.allegati?.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-slate-500 mb-1">Allegati</div>
                      <div className="flex flex-wrap gap-2">
                        {o.allegati.map((a) => (
                          <a key={a.id} href={fileUrl(a.storage_path)} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-dusty-700 hover:bg-dusty-50"
                            data-testid={`order-allegato-link-${a.id}`}>
                            <Paperclip className="h-3.5 w-3.5" /> {a.original_filename}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
        {filteredOrders.length === 0 && <div className="text-slate-400 py-16 text-center">{orders.length === 0 ? "Nessun ordine presente." : "Nessun ordine corrisponde alla ricerca."}</div>}
      </div>

      <Dialog open={!!att} onOpenChange={(v) => !v && setAtt(null)}>
        <DialogContent className="bg-white" data-testid="attestato-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Attestato corsista</DialogTitle>
            {att && <DialogDescription>{att.nome} · {att.corso}</DialogDescription>}
          </DialogHeader>
          {att && (
            <div className="space-y-4 py-1">
              {att.existing && (
                <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                  <a href={fileUrl(att.existing.storage_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-emerald-700 hover:underline">
                    <Download className="h-4 w-4" /> {att.existing.original_filename}
                  </a>
                  <button onClick={removeAtt} className="text-rose-500 hover:text-rose-700" data-testid="attestato-remove"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
              <div>
                <Label>{att.existing ? "Sostituisci file (opzionale)" : "File attestato *"}</Label>
                <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" className="mt-1.5"
                  onChange={(e) => setAtt({ ...att, file: e.target.files?.[0] || null })} data-testid="attestato-file" />
              </div>
              <div>
                <Label>Data di emissione certificato</Label>
                <Input type="date" value={att.data_emissione} onChange={(e) => setAtt({ ...att, data_emissione: e.target.value })} className="mt-1.5" data-testid="attestato-emissione" />
                <p className="text-xs text-slate-400 mt-1">Se il corso ha una validità impostata a catalogo, la scadenza viene calcolata in automatico da questa data.</p>
              </div>
              <div>
                <Label>Data di scadenza {att.data_emissione ? "(automatica se lasciata vuota)" : ""}</Label>
                <Input type="date" value={att.scadenza} onChange={(e) => setAtt({ ...att, scadenza: e.target.value })} className="mt-1.5" data-testid="attestato-scadenza" />
                <p className="text-xs text-slate-400 mt-1">Il partner riceverà un avviso (piattaforma + email) circa 30 giorni prima della scadenza.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAtt(null)}>Annulla</Button>
            <Button onClick={saveAtt} className="bg-dusty-600 hover:bg-dusty-700 text-white" data-testid="attestato-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sendDlg} onOpenChange={(v) => !v && setSendDlg(null)}>
        <DialogContent className="bg-white" data-testid="send-docs-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Invia documenti da firmare</DialogTitle>
            {sendDlg && <DialogDescription>Allega i documenti da far firmare ai corsisti. Verranno inviati al partner via email e salvati nell'ordine di {sendDlg.azienda}.</DialogDescription>}
          </DialogHeader>
          {sendDlg && (
            <div className="space-y-4 py-1">
              <div>
                <Label>Documenti (PDF, immagini, Word) *</Label>
                <Input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" className="mt-1.5"
                  onChange={(e) => {
                    const nf = Array.from(e.target.files || []);
                    e.target.value = "";
                    setSendDlg((d) => ({ ...d, files: [...d.files, ...nf.filter((n) => !d.files.some((x) => x.name === n.name && x.size === n.size))] }));
                  }} data-testid="send-docs-files" />
                <p className="text-xs text-slate-400 mt-1">Puoi selezionare più file insieme oppure aggiungerne altri diversi in più riprese.</p>
                {sendDlg.files.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="send-docs-list">
                    {sendDlg.files.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">
                        <span className="inline-flex items-center gap-1.5 text-slate-600 truncate"><Paperclip className="h-3.5 w-3.5 shrink-0" />{f.name}</span>
                        <button type="button" onClick={() => setSendDlg((d) => ({ ...d, files: d.files.filter((_, j) => j !== i) }))} className="text-slate-400 hover:text-rose-500" data-testid={`send-docs-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label>Messaggio per il partner (opzionale)</Label>
                <Textarea value={sendDlg.messaggio} onChange={(e) => setSendDlg({ ...sendDlg, messaggio: e.target.value })} className="mt-1.5" data-testid="send-docs-message" placeholder="Es. Far firmare a tutti i corsisti e ricaricare in «I Miei Attestati»." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDlg(null)}>Annulla</Button>
            <Button onClick={sendDocs} className="bg-dusty-600 hover:bg-dusty-700 text-white" data-testid="send-docs-submit"><Send className="h-4 w-4 mr-1.5" /> Invia al partner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fadDlg} onOpenChange={(v) => !v && setFadDlg(null)}>
        <DialogContent className="bg-white max-w-3xl w-[94vw]" data-testid="fad-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Invia credenziali FAD ai corsisti</DialogTitle>
            {fadDlg && <DialogDescription>Ordine di {fadDlg.azienda}. Ogni corsista riceve un'email precompilata con link, user e password. Le credenziali vengono salvate nell'ordine.</DialogDescription>}
          </DialogHeader>
          {fadDlg && (
            <div className="space-y-4 py-1 max-h-[70vh] overflow-y-auto pr-1">
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-600">Data attivazione</Label><Input type="date" value={fadDlg.data_attivazione} onChange={(e) => setFadDlg({ ...fadDlg, data_attivazione: e.target.value })} className="mt-1 h-9" data-testid="fad-data-attivazione" /></div>
                <div><Label className="text-xs text-slate-600">Data fine</Label><Input type="date" value={fadDlg.data_fine} onChange={(e) => setFadDlg({ ...fadDlg, data_fine: e.target.value })} className="mt-1 h-9" data-testid="fad-data-fine" /></div>
                <div><Label className="text-xs text-slate-600">Tutor assegnato</Label><Input value={fadDlg.tutor_nome} onChange={(e) => setFadDlg({ ...fadDlg, tutor_nome: e.target.value })} className="mt-1 h-9" placeholder="Nome tutor" data-testid="fad-tutor-nome" /></div>
                <div><Label className="text-xs text-slate-600">Tutor cell/whatsapp</Label><Input value={fadDlg.tutor_contatto} onChange={(e) => setFadDlg({ ...fadDlg, tutor_contatto: e.target.value })} className="mt-1 h-9" placeholder="Numero" data-testid="fad-tutor-contatto" /></div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-600">Copia al partner (email automatiche)</Label>
                  <Select value={fadDlg.email_partner_custom ? "__custom__" : (fadDlg.email_partner || "__none__")} onValueChange={onPartnerEmailChange}>
                    <SelectTrigger className="mt-1 h-9" data-testid="fad-email-partner-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nessuna copia al partner</SelectItem>
                      {(fadDlg.partnerEmails || []).map((em) => <SelectItem key={em} value={em}>{em}</SelectItem>)}
                      <SelectItem value="__custom__">Altra email…</SelectItem>
                    </SelectContent>
                  </Select>
                  {fadDlg.email_partner_custom && (
                    <Input value={fadDlg.email_partner} onChange={(e) => setFadDlg({ ...fadDlg, email_partner: e.target.value })} className="mt-2 h-9" placeholder="email@partner.it" data-testid="fad-email-partner" />
                  )}
                  {(fadDlg.partnerEmails || []).length === 0 && !fadDlg.email_partner_custom && (
                    <p className="text-[11px] text-slate-400 mt-1">Nessuna email automatica trovata per il partner: scegli «Altra email…» per inserirla a mano.</p>
                  )}
                </div>
                <label className="col-span-2 inline-flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                  <input type="checkbox" checked={fadDlg.avvisa_safe} onChange={(e) => setFadDlg({ ...fadDlg, avvisa_safe: e.target.checked })} data-testid="fad-avvisa-safe" className="h-4 w-4 accent-dusty-600" />
                  Notifica anche alla Safe (staff)
                </label>
                <div className="col-span-2"><Label className="text-xs text-slate-600">Messaggio sollecito (facoltativo)</Label><Input value={fadDlg.sollecito_msg || ""} onChange={(e) => setFadDlg({ ...fadDlg, sollecito_msg: e.target.value })} className="mt-1 h-9" placeholder="Testo extra per il sollecito al corsista" data-testid="fad-sollecito-msg" /></div>
                <p className="col-span-2 text-[11px] text-slate-400">Questi dati (attivazione, scadenza, tutor, copia partner, notifica Safe) valgono per tutti gli invii qui sotto.</p>
              </div>

              {fadDlg.rows.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Nessun corsista in questo ordine.</div>}
              {fadDlg.rows.map((r, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`fad-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-800">{r.nome} <span className="text-xs font-normal text-slate-400">· {r.corso}</span></div>
                    {r.sent_at && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`fad-sent-${i}`}>Inviata</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-slate-600">Piattaforma</Label>
                      <Select value={r.piattaforma} onValueChange={(v) => onFadPlatform(i, v)}>
                        <SelectTrigger className="mt-1 h-9" data-testid={`fad-piattaforma-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kattedra">Kattedra</SelectItem>
                          <SelectItem value="lcr">LCR Formazione</SelectItem>
                          <SelectItem value="safe">Safe Consulting</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs text-slate-600">Email destinatario</Label><Input value={r.email} onChange={(e) => setFadRow(i, { email: e.target.value })} className="mt-1 h-9" placeholder="email@corsista.it" data-testid={`fad-email-${i}`} /></div>
                    <div className="col-span-2"><Label className="text-xs text-slate-600">Link piattaforma</Label><Input value={r.link} onChange={(e) => setFadRow(i, { link: e.target.value })} className="mt-1 h-9" data-testid={`fad-link-${i}`} /></div>
                    <div><Label className="text-xs text-slate-600">Username</Label><Input value={r.username} onChange={(e) => setFadRow(i, { username: e.target.value })} className="mt-1 h-9" data-testid={`fad-username-${i}`} /></div>
                    <div><Label className="text-xs text-slate-600">Password</Label><Input value={r.password} onChange={(e) => setFadRow(i, { password: e.target.value })} className="mt-1 h-9" data-testid={`fad-password-${i}`} /></div>
                  </div>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[11px] text-slate-500 space-y-0.5" data-testid={`fad-invii-${i}`}>
                      {(r.invii || []).length === 0 ? (
                        <span className="text-slate-400">Nessun invio registrato</span>
                      ) : (
                        (r.invii || []).slice(-4).map((iv, k) => (
                          <div key={k} className="flex items-center gap-1.5">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${iv.tipo === "sollecito" ? "bg-amber-500" : "bg-emerald-500"}`} />
                            {iv.tipo === "sollecito" ? "Sollecito" : "Credenziali"} · {fmtDate((iv.sent_at || "").slice(0, 10))}
                            {iv.email_sent ? "" : " (email non inviata)"}
                            {iv.email_partner ? ` · copia a ${iv.email_partner}` : ""}
                          </div>
                        ))
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => sendSollecito(i)} disabled={r.sollecitando} className="h-8 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`fad-sollecito-${i}`}>
                        <Bell className="h-4 w-4 mr-1.5" /> {r.sollecitando ? "Invio…" : "Sollecito"}
                      </Button>
                      <Button size="sm" onClick={() => sendFad(i)} disabled={r.sending} className="bg-dusty-600 hover:bg-dusty-700 text-white h-8" data-testid={`fad-send-${i}`}>
                        <Send className="h-4 w-4 mr-1.5" /> {r.sending ? "Invio…" : (r.sent_at ? "Reinvia" : "Invia credenziali")}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFadDlg(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
